import { IFilter } from "@/types";
import { getEmployeesOffersV2 } from "@/api/offers";
import { getAffiliateSearch } from "@/api/user";
import { getAdvertisersSearch } from "@/api/revenue";
import { addDays, subDays } from "date-fns";
import { translateConversionStatus } from "@/utils/translate";
import { CreatedFilters } from "@/services/filters/filters";
import { filterClass } from '@/services/filters/filterClass';
import { IListItem } from "@/components-UI/list/types";
import { IConversion } from "@/api/types/conversions-report";
import store from "@/store";
import { USER_ROLES } from "@/mappings/user-roles";

// столбцы отчёта по конверсиям, доступные только администратору (роль ROLE_ADMIN)
export const CONVERSION_ADMIN_ONLY_COLUMNS = ['hold_source', 'hold_days', 'hold_date'];

export class ConversionFilters extends CreatedFilters {
    constructor() {
        super();
    }

    private getAllowedDates(item: { start: string, end: string } | null, drag: { start: string, end: string }): any {
        const isSelected = item?.start && item?.end;
        const isDragging = drag?.start && drag?.end;

        if (isDragging && !isSelected) {
            const max = addDays(new Date(drag.start), 149);
            const min = subDays(new Date(drag.end), 149);

            return {
                start: min,
                end: max > new Date()
                    ? new Date()
                    : max,
            };
        }

        return { start: null, end: new Date() };
    }

    getFilters(): IFilter[] {
        return [
            new filterClass.Datepicker({
                col: 'Дата',
                id: 'date',
                label: 'Период',
                pickerType: 'date',
                range: true,
                allowedDates: this.getAllowedDates,
                excludingItems: ['За текущий год'],
                clearable: true,
                isOnlyPlaceholder: true,
            }),
            new filterClass.Autocomplete({
                col: 1,
                id: 'advertiser_id',
                label: 'Рекламодатель',
                multiple: true,
                apiMethod: getAdvertisersSearch,
                itemValue: 'id',
                itemText: 'name',
            }),
            new filterClass.Autocomplete({
                col: 1,
                id: 'offer_id',
                apiMethod: getEmployeesOffersV2,
                key: 'search',
                label: 'Оффер',
                itemValue: 'legacy_id',
                itemText: 'name',
                multiple: true,
                multiPaste: true,
                minSearchLength: 2,
            }),
            new filterClass.Multiselect({
                col: 1,
                id: 'goal_id',
                itemValue: 'legacy_id',
                itemText: (item: any): string => `${item.legacy_id} ${item.name}`,
                label: 'Цель',
                noDataText: 'Выберите оффер',
                clearable: true,
                disabled: true,
                type: 'number',
                template: (item: any): string => `${item.legacy_id} ${item.name}`,
                tooltip: 'Сначала отфильтруйте отчёт по офферу',
            }),
            new filterClass.Multiselect({
                col: 1,
                id: 'status',
                label: 'Статус конверсии',
                items: [{ id: 'approved' }, { id: 'rejected' }, { id: 'pending' }],
                select: [],
                type: 'string',
                defaultValue: ['approved'],
                itemValue: 'id',
                template: ({ id }) => this.translateStatus(id, translateConversionStatus),
                itemText: ({ id }) => this.translateStatus(id, translateConversionStatus),
            }),
            new filterClass.Autocomplete({
                col: 2,
                id: 'master_account_id',
                label: 'Мастер-аккаунт',
                apiMethod: getAffiliateSearch,
                itemValue: 'id',
                multiple: true,
                multiPaste: true,
                template: (i) => `${i.id} ${i.first_name} ${i.last_name} ${i.email}`,
            }),
            new filterClass.Autocomplete({
                col: 2,
                id: 'buffer_account_id',
                label: 'Буферный аккаунт',
                apiMethod: getAffiliateSearch,
                itemValue: 'id',
                itemText: 'name',
                template: (i) => `${i.id} ${i.first_name} ${i.last_name}`,
                multiple: true,
                multiPaste: true,
            }),
            new filterClass.Select({
                col: 2,
                id: 'buffer_account_used',
                label: 'Буфер',
                items: [
                    { text: 'Да', value: true },
                    { text: 'Нет', value: false },
                ],
                clearable: true,
                type: 'boolean',
            }),
            new filterClass.Autocomplete({
                col: 2,
                id: 'affiliate_id',
                label: 'Партнер',
                apiMethod: getAffiliateSearch,
                itemValue: 'id',
                itemText: 'name',
                template: (i) => `${i.id} ${i.first_name} ${i.last_name} ${i.email}`,
                multiple: true,
                multiPaste: true,
            }),
            new filterClass.ComboboxArray({
                col: 2,
                id: 'affiliate_invoice_id',
                label: 'ID счета',
                supValue: '',
                multiPaste: true,
            }),
            new filterClass.ComboboxArray({
                col: 3,
                id: 'adv_sub',
                label: 'Sub ID рекламодателя',
                disabled: true,
                tooltip: 'Сначала отфильтруйте отчёт по офферу',
                multiPaste: true,
            }),
            new filterClass.ComboboxArray({
                col: 3,
                id: 'conversion_id',
                label: 'Conversion ID',
                multiPaste: true,
            }),
            new filterClass.ComboboxArray({
                col: 3,
                id: 'click_id',
                label: 'Transaction ID',
                multiPaste: true,
            }),
            new filterClass.ComboboxArray({
                col: 3,
                id: 'tracker_click_id',
                label: 'ID клика',
                multiPaste: true,
            }),
            new filterClass.ComboboxArray({
                col: 3,
                id: 'client_id',
                label: 'ID клиента',
                multiPaste: true,
            }),
        ];
    }
}

export const conversionHeaders = [
    {
        text: 'ID Conversion (Lead ID)',
        sortable: false,
        value: 'conversion_id',
        width: '100px',
        align: 'center',
        fixed: true,
    },
    { text: 'ID Transaction', sortable: false, value: 'click_id', align: 'left' },
    { text: 'ID клиента', sortable: false, value: 'client_id', width: '190' },
    { text: 'ID счёта', sortable: false, value: 'affiliate_invoice_id', align: 'left' },
    { text: 'ID клика', sortable: false, value: 'tracker_click_id', align: 'left' },
    { text: 'Origin', sortable: false, value: 'origin' },
    { text: 'Type', sortable: false, value: 'type' },
    { text: 'Партнёр', sortable: false, value: 'affiliate_name' },
    { text: 'ID партнёра', sortable: false, value: 'affiliate_id' },
    { text: 'Менеджер', sortable: false, value: 'affiliate_manager_name' },
    { text: 'ID менеджера', sortable: false, value: 'affiliate_manager_id' },
    { text: 'Тип договора', sortable: false, value: 'master_account_contract_type' },
    { text: 'Маркировка', sortable: false, value: 'master_account_marking' },
    { text: 'Рекламодатель', sortable: false, value: 'advertiser_name' },
    { text: 'ID рекламодателя', sortable: false, value: 'advertiser_id' },
    { text: 'Менеджер рекламодателя', sortable: false, value: 'advertiser_manager_name' },
    { text: 'ID менеджера рекламодателя', sortable: false, value: 'advertiser_manager_id' },
    { text: 'ID мастера', sortable: false, value: 'master_account_id' },
    { text: 'Мастер', sortable: false, value: 'master_account_name' },
    { text: 'Буфер', sortable: false, value: 'buffer_account_used' },
    { text: 'ID Буфера', sortable: false, value: 'buffer_account_id' },
    { text: 'Имя буфера', sortable: false, value: 'buffer_account_name' },
    { text: 'Оффер', sortable: false, value: 'offer_name', align: 'left' },
    { text: 'ID оффера', sortable: false, value: 'offer_id', align: 'left' },
    { text: 'Цель', sortable: false, value: 'goal_name' },
    { text: 'ID цели', sortable: false, value: 'goal_id', align: 'left' },
    { text: 'Валюта', sortable: false, value: 'currency' },
    { text: 'Сумма продажи', sortable: false, value: 'sale_amount', width: '100px' },
    { text: 'Доход', sortable: false, align: 'right', value: 'revenue' },
    { text: 'Выплата', sortable: false, align: 'right', value: 'payout' },
    { text: 'Входящая цена', sortable: false, align: 'right', value: 'full_price' },
    { text: 'Входящая цена без НДС', sortable: false, align: 'right', value: 'price_after_vat' },
    { text: 'Источник холда', sortable: false, align: 'right', value: 'hold_source' },
    { text: 'Время холда', sortable: false, align: 'right', value: 'hold_days' },
    { text: 'Дата холда', sortable: false, align: 'right', value: 'hold_date' },
    { text: 'Прибыль', sortable: false, value: 'profit', align: 'right' },
    { text: 'Период счёта', sortable: false, value: 'affiliate_invoice_period', align: 'left' },
    { text: 'Время обработки', sortable: false, value: 'processing_time', align: 'left' },
    { text: 'Период продажи', sortable: false, value: 'sale_time_formatted', align: 'left' },
    { text: 'Дата клика', sortable: false, value: 'click_date', width: '170px', align: 'left' },
    { text: 'Дата конверсии', sortable: false, value: 'conversion_date', width: '170px', align: 'right' },
    { text: 'Дата апрува', sortable: false, value: 'approval_date', width: '170px', align: 'left' },
    { text: 'Дата создания', sortable: false, value: 'creation_date', width: '170px', align: 'left' },
    { text: 'Дата обновления', sortable: false, value: 'update_date', width: '170px', align: 'left' },
    { text: 'Статус', sortable: false, align: 'center', value: 'status' },
    { text: 'IP сессии', sortable: false, value: 'session_ip' },
    { text: 'User Agent', sortable: false, value: 'user_agent' },
    { text: 'Referrer', sortable: false, value: 'referer_url' },
    { text: 'Sub ID рекламодателя', sortable: false, value: 'adv_sub' },
    { text: 'Sub ID1', sortable: false, value: 'subid1', align: 'left' },
    { text: 'Sub ID2', sortable: false, value: 'subid2', align: 'left' },
    { text: 'Sub ID3', sortable: false, value: 'subid3', align: 'left' },
    { text: 'Sub ID4', sortable: false, value: 'subid4', align: 'left' },
    { text: 'Sub ID5', sortable: false, value: 'subid5', align: 'left' },
    { text: 'Sub ID6', sortable: false, value: 'subid6', align: 'left' },
    { text: 'Sub ID7', sortable: false, value: 'subid7', align: 'left' },
    { text: 'Sub ID8', sortable: false, value: 'subid8', align: 'left' },
    { text: 'Sub ID9', sortable: false, value: 'subid9', align: 'left' },
    { text: 'Sub ID10', sortable: false, value: 'subid10', align: 'left' },
    { text: 'Sub ID11', sortable: false, value: 'subid11', align: 'left' },
    { text: 'Sub ID12', sortable: false, value: 'subid12', align: 'left' },
    { text: 'Sub ID13', sortable: false, value: 'subid13', align: 'left' },
    { text: 'Sub ID14', sortable: false, value: 'subid14', align: 'left' },
    { text: 'Sub ID15', sortable: false, value: 'subid15', align: 'left' },
    { text: 'Sub ID16', sortable: false, value: 'subid16', align: 'left' },
    { text: 'Sub ID17', sortable: false, value: 'subid17', align: 'left' },
    { text: 'Sub ID18', sortable: false, value: 'subid18', align: 'left' },
    { text: 'Sub ID19', sortable: false, value: 'subid19', align: 'left' },
    { text: 'Sub ID20', sortable: false, value: 'subid20', align: 'left' },
    { text: 'Sub ID21', sortable: false, value: 'subid21', align: 'left' },
    { text: 'Sub ID22', sortable: false, value: 'subid22', align: 'left' },
    { text: 'Sub ID23', sortable: false, value: 'subid23', align: 'left' },
    { text: 'Sub ID24', sortable: false, value: 'subid24', align: 'left' },
    { text: 'Sub ID25', sortable: false, value: 'subid25', align: 'left' },
];
export const conversionRoutes = [
    {
        routerName: 'conversion',
        routeParam: 'id',
        extraRouteParamName: 'conversion_id',
        slot: 'item.conversion_id',
        text: 'conversion_id',
    },
    {
        routeParam: 'id',
        extraRouteParamName: 'offer_id',
        routerName: 'offer',
        slot: 'item.offer_name',
        // костыль, если offer_name пустой, тогда приходит пустой массив с бека, иначе приходит строка
        template: (item: any): string => Array.isArray(item.offer_name) ? item.offer_name[0] : item.offer_name,
    },
    {
        routerName: 'offer',
        routeParam: 'id',
        extraRouteParamName: 'offer_id',
        slot: 'item.offer_id',
        text: 'offer_id',
    },
    {
        routeParam: 'id',
        extraRouteParamName: 'offer_id',
        routerName: 'offer',
        slot: 'item.goal_name',
        // костыль, если goal_name пустой, тогда приходит пустой массив с бека, иначе приходит строка
        template: (item: any): string => Array.isArray(item.goal_name) ? item.goal_name[0] : item.goal_name,
    },
    {
        routeParam: 'id',
        extraRouteParamName: 'advertiser_id',
        routerName: 'advertiser',
        slot: 'item.advertiser_name',
        text: 'advertiser_name',
    },
    {
        routeParam: 'id',
        extraRouteParamName: 'advertiser_id',
        routerName: 'advertiser',
        slot: 'item.advertiser_id',
        text: 'advertiser_id',
    },
    {
        routeParam: 'id',
        extraRouteParamName: 'affiliate_invoice_id',
        routerName: 'affiliate-invoice',
        slot: 'item.affiliate_invoice_id',
        text: 'affiliate_invoice_id',
    },
    {
        routeParam: 'id',
        extraRouteParamName: 'affiliate_manager_id',
        routerName: 'user-detail-page',
        slot: 'item.affiliate_manager_id',
        text: 'affiliate_manager_id',
        tooltip: (item: any): string => item.affiliate_manager_id,
    },
    {
        routeParam: 'id',
        extraRouteParamName: 'affiliate_manager_id',
        routerName: 'user-detail-page',
        slot: 'item.affiliate_manager_name',
        text: 'affiliate_manager_name',
        tooltip: (item: any): string => item.affiliate_manager_name,
    },
    {
        routeParam: 'id',
        extraRouteParamName: 'offer_id',
        routerName: 'offer',
        slot: 'item.goal_id',
        text: 'goal_id',
    },
    {
        routeParam: 'id',
        extraRouteParamName: 'affiliate_id',
        routerName: 'affiliate',
        slot: 'item.affiliate_id',
        text: 'affiliate_id',
    },
    {
        routeParam: 'id',
        extraRouteParamName: 'affiliate_id',
        routerName: 'affiliate',
        slot: 'item.affiliate_name',
        text: 'affiliate_name',
    },
    {
        routeParam: 'id',
        extraRouteParamName: 'master_account_id',
        routerName: 'affiliate',
        slot: 'item.master_account_id',
        text: 'master_account_id',
    },
    {
        routeParam: 'id',
        extraRouteParamName: 'master_account_id',
        routerName: 'affiliate',
        slot: 'item.master_account_name',
        text: 'master_account_name',
    },
];

export const columnsConversionsReport: Array<{ base: string, letters: string }> = [
    { base: 'ID Conversion (Lead ID)', letters: 'conversion_id' },
    { base: 'ID Transaction', letters: 'click_id' },
    { base: 'ID партнера', letters: 'affiliate_id' },
    { base: 'ID оффера', letters: 'offer_id' },
    { base: 'Оффер', letters: 'offer_name' },
    { base: 'ID цели', letters: 'goal_id' },
    { base: 'Цель', letters: 'goal_name' },
    { base: 'Период счёта', letters: 'affiliate_invoice_period' },
    { base: 'Время обработки', letters: 'processing_time' },
    { base: 'Период продажи', letters: 'sale_time_formatted' },
    { base: 'Дата апрува', letters: 'approval_date' },
    { base: 'Дата клика', letters: 'click_date' },
    { base: 'Дата конверсии', letters: 'conversion_date' },
    { base: 'Дата создания', letters: 'creation_date' },
    { base: 'Дата конверсии', letters: 'conversion_date' },
    { base: 'Дата обновления', letters: 'update_date' },
    { base: 'ID Буфера', letters: 'buffer_account_id' },
    { base: 'Буфер', letters: 'buffer_account_used' },
    { base: 'Имя Буфера', letters: 'buffer_account_name' },
    { base: 'ID мастера', letters: 'master_account_id' },
    { base: 'Мастер', letters: 'master_account_name' },
    { base: 'IP сессии', letters: 'session_ip' },
    { base: 'User Agent', letters: 'user_agent' },
    { base: 'Referrer', letters: 'referer_url' },
    { base: 'ID клиента', letters: 'client_id' },
    { base: 'Sub ID рекламодателя', letters: 'adv_sub' },
    { base: 'Sub ID1', letters: 'subid1' },
    { base: 'Sub ID2', letters: 'subid2' },
    { base: 'Sub ID3', letters: 'subid3' },
    { base: 'Sub ID4', letters: 'subid4' },
    { base: 'Sub ID5', letters: 'subid5' },
    { base: 'Sub ID6', letters: 'subid6' },
    { base: 'Sub ID7', letters: 'subid7' },
    { base: 'Sub ID8', letters: 'subid8' },
    { base: 'Sub ID9', letters: 'subid9' },
    { base: 'Sub ID10', letters: 'subid10' },
    { base: 'Sub ID11', letters: 'subid11' },
    { base: 'Sub ID12', letters: 'subid12' },
    { base: 'Sub ID13', letters: 'subid13' },
    { base: 'Sub ID14', letters: 'subid14' },
    { base: 'Sub ID15', letters: 'subid15' },
    { base: 'Sub ID16', letters: 'subid16' },
    { base: 'Sub ID17', letters: 'subid17' },
    { base: 'Sub ID18', letters: 'subid18' },
    { base: 'Sub ID19', letters: 'subid19' },
    { base: 'Sub ID20', letters: 'subid20' },
    { base: 'Sub ID21', letters: 'subid21' },
    { base: 'Sub ID22', letters: 'subid22' },
    { base: 'Sub ID23', letters: 'subid23' },
    { base: 'Sub ID24', letters: 'subid24' },
    { base: 'Sub ID25', letters: 'subid25' },
    { base: 'Статус', letters: 'status' },
    { base: 'Валюта', letters: 'currency' },
    { base: 'Сумма продажи', letters: 'sale_amount' },
    { base: 'Выплата', letters: 'payout' },
    { base: 'Входящая цена', letters: 'full_price' },
    { base: 'Входящая цена без НДС', letters: 'price_after_vat' },
    { base: 'Источник холда', letters: 'hold_source' },
    { base: 'Время холда', letters: 'hold_days' },
    { base: 'Дата холда', letters: 'hold_date' },
    { base: 'Доход', letters: 'revenue' },
    { base: 'Прибыль', letters: 'profit' },
    { base: 'ID счёта', letters: 'invoice_id' },
];

export const conversionFilterCols = [
    {
        name: '',
        items: [
            { title: 'ID Transaction', value: 'click_id' },
            { title: 'ID счёта', value: 'affiliate_invoice_id' },
            { title: 'ID клиента', value: 'client_id' },
            { title: 'ID клика', value: 'tracker_click_id' },
            { title: 'Origin', value: 'origin' },
            { title: 'Type', value: 'type' },
        ],
    },
    {
        name: '',
        items: [
            { title: 'Партнёр', value: 'affiliate_name' },
            { title: 'ID партнёра', value: 'affiliate_id' },
            { title: 'Мастер', value: 'master_account_name' },
            { title: 'ID мастера', value: 'master_account_id' },
            { title: 'Буфер', value: 'buffer_account_used' },
            { title: 'Имя буфера', value: 'buffer_account_name' },
            { title: 'ID Буфера', value: 'buffer_account_id' },
            { title: 'Менеджер', value: 'affiliate_manager_name' },
            { title: 'ID менеджера', value: 'affiliate_manager_id' },
            { title: 'Тип договора', value: 'master_account_contract_type' },
            { title: 'Маркировка', value: 'master_account_marking' },
        ],
    },
    {
        name: '',
        items: [
            { title: 'Рекламодатель', value: 'advertiser_name' },
            { title: 'ID рекламодателя', value: 'advertiser_id' },
            { title: 'Менеджер рекламодателя', value: 'advertiser_manager_name' },
            { title: 'ID менеджера рекламодателя', value: 'advertiser_manager_id' },
            { title: 'Оффер', value: 'offer_name' },
            { title: 'ID Оффера', value: 'offer_id' },
            { title: 'Цель', value: 'goal_name' },
            { title: 'ID цели', value: 'goal_id' },
        ],
    },
    {
        name: '',
        items: [
            { title: 'Валюта', value: 'currency' },
            { title: 'Сумма продажи', value: 'sale_amount' },
            { title: 'Доход', value: 'revenue' },
            { title: 'Выплата', value: 'payout' },
            { title: 'Входящая цена', value: 'full_price' },
            { title: 'Входящая цена без НДС', value: 'price_after_vat' },
            { title: 'Источник холда', value: 'hold_source' },
            { title: 'Время холда', value: 'hold_days' },
            { title: 'Дата холда', value: 'hold_date' },
            { title: 'Прибыль', value: 'profit' },
            { title: 'Период счёта', value: 'affiliate_invoice_period' },
            { title: 'Время обработки', value: 'processing_time' },
            { title: 'Период продажи', value: 'sale_time_formatted' },
        ],
    },
    {
        name: '',
        items: [
            { title: 'Дата клика', value: 'click_date' },
            { title: 'Дата конверсии', value: 'conversion_date' },
            { title: 'Дата апрува', value: 'approval_date' },
            { title: 'Дата создания', value: 'creation_date' },
            { title: 'Дата обновления', value: 'update_date' },
            { title: 'Статус', value: 'status' },
            { title: 'IP сессии', value: 'session_ip' },
            { title: 'User Agent', value: 'user_agent' },
            { title: 'Referrer', value: 'referer_url' },
            { title: 'Sub ID рекламодателя', value: 'adv_sub' },
        ],
    },
    {
        value: false,
        name: 'Sub ID',
        items: [
            { title: 'Sub ID 1', value: 'subid1' },
            { title: 'Sub ID 2', value: 'subid2' },
            { title: 'Sub ID 3', value: 'subid3' },
            { title: 'Sub ID 4', value: 'subid4' },
            { title: 'Sub ID 5', value: 'subid5' },
            { title: 'Sub ID 6', value: 'subid6' },
            { title: 'Sub ID 7', value: 'subid7' },
            { title: 'Sub ID 8', value: 'subid8' },
            { title: 'Sub ID 9', value: 'subid9' },
            { title: 'Sub ID 10', value: 'subid10' },
            { title: 'Sub ID 11', value: 'subid11' },
            { title: 'Sub ID 12', value: 'subid12' },
            { title: 'Sub ID 13', value: 'subid13' },
            { title: 'Sub ID 14', value: 'subid14' },
            { title: 'Sub ID 15', value: 'subid15' },
            { title: 'Sub ID 16', value: 'subid16' },
            { title: 'Sub ID 17', value: 'subid17' },
            { title: 'Sub ID 18', value: 'subid18' },
            { title: 'Sub ID 19', value: 'subid19' },
            { title: 'Sub ID 20', value: 'subid20' },
            { title: 'Sub ID 21', value: 'subid21' },
            { title: 'Sub ID 22', value: 'subid22' },
            { title: 'Sub ID 23', value: 'subid23' },
            { title: 'Sub ID 24', value: 'subid24' },
            { title: 'Sub ID 25', value: 'subid25' },
        ],
    },
];

type ConversionItem = IListItem<IConversion>;
type ConversionCardName = 'main' | 'affiliate' | 'offer' | 'finance' | 'info' | 'subIds';
export type ConversionCards = {
    [key in ConversionCardName]: {
        title: string,
        items: ConversionItem[],
        col: number
    }
}

// -- Single
export const useConversionSingle = (): { getCards: (data: IConversion) => ConversionCards; fields: string[] } => {
    const fields = [
        'conversion_id',
        'click_id',
        'tracker_click_id',
        'origin',
        'type',
        'client_id',
        'click_date',
        'conversion_date',
        'approval_date',
        'creation_date',
        'update_date',
        'processing_time',
        'sale_time_formatted',
        'status',
        'affiliate_id',
        'affiliate_name',
        'user_agent',
        'referer_url',
        'master_account_id',
        'master_account_name',
        'buffer_account_used',
        'buffer_account_id',
        'buffer_account_name',
        'affiliate_manager_id',
        'affiliate_manager_name',
        'offer_id',
        'offer_name',
        'goal_id',
        'goal_name',
        'advertiser_id',
        'advertiser_name',
        'advertiser_manager_id',
        'advertiser_manager_name',
        'adv_sub',
        'currency',
        'sale_amount',
        'revenue',
        'payout',
        'profit',
        'affiliate_invoice_id',
        'affiliate_invoice_period',
        'subid1',
        'subid2',
        'subid3',
        'subid4',
        'subid5',
        'subid6',
        'subid7',
        'subid8',
        'subid9',
        'subid10',
        'subid11',
        'subid12',
        'subid13',
        'subid14',
        'subid15',
        'subid16',
        'subid17',
        'subid18',
        'subid19',
        'subid20',
        'subid21',
        'subid22',
        'subid23',
        'subid24',
        'subid25',
        'session_ip',
        'hold_source',
        'hold_days',
        'hold_date',
    ];

    const main: ConversionItem[] = [
        {
            itemType: 'code',
            label: 'ID Conversion',
            valueKey: 'conversion_id',
            value: '',
            isFullWidthValue: true,
            isCopyOnClick: true,
        },
        {
            itemType: 'code',
            label: 'ID Transaction',
            valueKey: 'click_id',
            value: '',
            isFullWidthValue: true,
            isCopyOnClick: true,
        },
        {
            itemType: 'code',
            label: 'ID клика',
            valueKey: 'tracker_click_id',
            value: '',
            isFullWidthValue: true,
            isCopyOnClick: true,
        },
        {
            itemType: 'code',
            label: 'ID клиента',
            valueKey: 'client_id',
            value: '',
            isFullWidthValue: true,
            isCopyOnClick: true,
        },
        { itemType: 'text', label: 'Origin', valueKey: 'origin', value: '' },
        { itemType: 'text', label: 'Type', valueKey: 'type', value: '' },
        {
            itemType: 'date',
            label: 'Дата клика',
            valueKey: 'click_date',
            value: '',
            isSeconds: true,
            isShowTime: true,
        },
        {
            itemType: 'date',
            label: 'Дата конверсии',
            valueKey: 'conversion_date',
            value: '',
            isSeconds: true,
            isShowTime: true,
        },
        {
            itemType: 'date',
            label: 'Дата аппрува',
            valueKey: 'approval_date',
            value: '',
            isSeconds: true,
            isShowTime: true,
        },
        {
            itemType: 'date',
            label: 'Дата создания',
            valueKey: 'creation_date',
            value: '',
            isSeconds: true,
            isShowTime: true,
        },
        {
            itemType: 'date',
            label: 'Дата обновления',
            valueKey: 'update_date',
            value: '',
            isSeconds: true,
            isShowTime: true,
        },
        {
            itemType: 'text',
            label: 'Время на обр.',
            valueKey: 'processing_time',
            value: '',
            hint: 'Время с даты создания до даты конверсии',
            valueSlot: 'difference',
        },
        {
            itemType: 'text',
            label: 'Период продажи',
            valueKey: 'sale_time_formatted',
            value: '',
            hint: 'Время с даты клика до даты конверсии',
            valueSlot: 'difference',
        },
        {
            itemType: 'text',
            label: 'Статус',
            valueKey: 'status',
            value: '',
            valueSlot: 'status',
        },
    ];

    const affiliate: ConversionItem[] = [
        {
            itemType: 'route',
            label: 'Партнёр',
            valueKey: 'affiliate_id',
            value: '',
            routeName: 'affiliate',
            routeText: 'affiliate_id',
            additionalText: 'affiliate_name',
            isCopyTooltip: true,
        },
        {
            itemType: 'route',
            label: 'Мастер',
            valueKey: 'master_account_id',
            value: '',
            routeName: 'affiliate',
            routeText: 'master_account_id',
            additionalText: 'master_account_name',
            isCopyTooltip: true,
        },
        {
            itemType: 'boolean',
            label: 'Буфер',
            valueKey: 'buffer_account_used',
            value: '',
            trueText: 'Да',
            falseText: 'Нет',
        },
        {
            itemType: 'route',
            label: 'Буферный аккаунт',
            valueKey: 'buffer_account_id',
            value: '',
            routeName: 'affiliate',
            routeText: 'buffer_account_id',
            additionalText: 'buffer_account_name',
            isCopyTooltip: true,
        },
        {
            itemType: 'text',
            label: 'Менеджер',
            valueKey: 'affiliate_manager_name',
            value: '',
        },
    ];

    const offer: ConversionItem[] = [
        {
            itemType: 'route',
            label: 'Оффер',
            valueKey: 'offer_id',
            value: '',
            routeName: 'offer',
            routeText: 'offer_id',
            additionalText: 'offer_name',
            isCopyTooltip: true,
        },
        {
            itemType: 'route',
            label: 'Цель',
            valueKey: 'offer_id',
            value: '',
            routeName: 'offer',
            routeText: 'goal_id',
            additionalText: 'goal_name',
            isCopyTooltip: true,
        },
        {
            itemType: 'route',
            label: 'Рекламодатель',
            valueKey: 'advertiser_id',
            value: '',
            routeName: 'advertiser',
            routeText: 'advertiser_id',
            additionalText: 'advertiser_name',
            isCopyTooltip: true,
        },
        { itemType: 'text', label: 'Менеджер', valueKey: 'advertiser_manager_name', value: '' },
        { itemType: 'text', label: 'Sub ID рекл.', valueKey: 'adv_sub', value: '' },
    ];

    const finance: ConversionItem[] = [
        {
            itemType: 'text',
            label: 'Валюта',
            valueKey: 'currency',
            value: '',
        },
        {
            itemType: 'number',
            label: 'Сумма продажи',
            valueKey: 'sale_amount',
            value: '',
            hint: 'Sale amount',
        },
        {
            itemType: 'number',
            label: 'Доход',
            valueKey: 'revenue',
            value: '',
        },
        {
            itemType: 'number',
            label: 'Выплата',
            valueKey: 'payout',
            value: '',
        },
        {
            itemType: 'number',
            label: 'Прибыль',
            valueKey: 'profit',
            value: '',
        },
        {
            itemType: 'route',
            label: 'ID счёта',
            valueKey: 'affiliate_invoice_id',
            value: '',
            routeName: 'invoice',
            routeText: 'affiliate_invoice_id',
            isCopyTooltip: true,
        },
        {
            itemType: 'date',
            label: 'Период счёта',
            valueKey: 'affiliate_invoice_period',
            value: '',
            dateFormat: 'dd MMMM yyyy',
        },
        {
            itemType: 'text',
            label: 'Источник холда',
            valueKey: 'hold_source',
            value: '',
            valueSlot: 'hold_source',
        },
        {
            itemType: 'text',
            label: 'Время холда',
            valueKey: 'hold_days',
            value: '',
            valueSlot: 'hold_days',
        },
        {
            itemType: 'date',
            label: 'Дата холда',
            valueKey: 'hold_date',
            value: '',
            dateFormat: 'dd MMMM yyyy',
        },
    ];
    const info: ConversionItem[] = [
        { itemType: 'text', label: 'User IP', valueKey: 'session_ip', value: '' },
        { itemType: 'text', label: 'User-Agent', valueKey: 'user_agent', value: '' },
        { itemType: 'code', label: 'Referrer URL', valueKey: 'referer_url', value: '' },
    ];

    const subIds: ConversionItem[] = Array.from({ length: 25 }, (_, index) => ({
        itemType: 'code',
        label: `Sub ID ${index + 1}`,
        valueKey: `subid${index + 1}`,
        isCopyOnClick: true,
        isFullWidthValue: true,
        value: '',
    }));

    const cards: ConversionCards = {
        main: { title: 'Основное', items: main, col: 0 },
        affiliate: { title: 'Партнёр', items: affiliate, col: 1 },
        offer: { title: 'Оффер', items: offer, col: 1 },
        finance: { title: 'Доход и выплата', items: finance, col: 0 },
        info: { title: 'Информация о клиенте', items: info, col: 1 },
        subIds: { title: 'Sub IDs', items: subIds, col: 1 },
    };

    const prepareFields = (data: IConversion) => {
        for (const card of Object.values(cards)) {
            card.items.forEach((item) => {
                const key = item.valueKey!;

                if (item.itemType === 'boolean') {
                    item.value = data[key] ?? '';
                } else {
                    item.value = `${data[key] ?? ''}`;
                }
            });
        }
    };

    const getCards = (data: IConversion) => {
        prepareFields(data);
        cards.subIds.items = cards.subIds.items.filter(item => item.value !== '');

        return cards;
    };

    // hold-поля доступны только администратору — убираем их и из запроса (fields),
    // и из карточки, чтобы не-админ не видел данные холда даже на детальной странице
    const isAdmin = (store.getters['authModule/GET_USER']?.roles ?? []).includes(USER_ROLES.ADMIN);
    if (!isAdmin) {
        cards.finance.items = cards.finance.items.filter(
            (item) => !CONVERSION_ADMIN_ONLY_COLUMNS.includes(item.valueKey!),
        );
        return { getCards, fields: fields.filter((field) => !CONVERSION_ADMIN_ONLY_COLUMNS.includes(field)) };
    }

    return { getCards, fields };
};
